import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { gql } from 'graphql-tag';
import { subDays, format } from 'date-fns';

// GraphQL query to get the count of unique hostnames with unviewed title changes
// This counts hostnames rather than individual changes to match the card grouping in the UI
const GET_TITLE_COUNT = gql`
  query GetTitleCount($startDate: timestamp!, $endDate: timestamp!) {
    live_domains_aggregate(
      where: {
        domain_title_associations: {
          viewed_at: {_is_null: true},
          times: {_gte: $startDate, _lte: $endDate},
          domain_title: {
            is_interesting: {_eq: true}
          }
        }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

// Create the context
export const TitleCountContext = createContext(null);

export function TitleCountProvider({ children }) {
  const [count, setCount] = useState(0);
  
  // Memoize dates
  const startDate = useMemo(() => 
    format(subDays(new Date(), 30), 'yyyy-MM-dd'), 
  []);
  const endDate = useMemo(() => 
    format(new Date(), 'yyyy-MM-dd HH:mm:ss'), 
  []);

  // Updated query config
  const { data, loading, refetch } = useQuery(GET_TITLE_COUNT, {
    variables: { startDate, endDate },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    pollInterval: 600000,
    notifyOnNetworkStatusChange: false,
    onError: (error) => console.error('Error fetching title change count:', error)
  });

  // Remove manual polling logic
  useEffect(() => {
    const newCount = data?.title_changes?.length || 0;
    setCount(prev => prev !== newCount ? newCount : prev);
  }, [data]);

  // Use useMemo to memoize the context value
  const contextValue = useMemo(() => ({ 
    count, 
    setCount, 
    loading, 
    refetch 
  }), [count, loading, refetch]);

  return (
    <TitleCountContext.Provider value={contextValue}>
      {children}
    </TitleCountContext.Provider>
  );
}

TitleCountProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useTitleCount() {
  const context = useContext(TitleCountContext);
  if (!context) {
    throw new Error('useTitleCount must be used within a TitleCountProvider');
  }
  return context;
} 